import { Maybe } from "graphql/jsutils/Maybe"
import { Money, MoneyInput } from "@oysterhr/schema"
import { currenciesByCurrencyCode, currencyCodeToSymbol } from "../localizationHelpers"

type FormatOptions = {
  symbolize?: boolean
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

/**
 * @deprecated
 * Please use SimpleMoney from utils/simpleMoney.ts
 */
export const moneyFormat = (amount: Maybe<number>, currencyCode: Maybe<string>): string | null => {
  if (amount == null || currencyCode == null) {
    return null
  }
  return `${currencyCode} ${amount.toLocaleString()}`
}

/**
 * @deprecated
 * Please use SimpleMoney from utils/simpleMoney.ts
 */
export const moneyFormatWithCurrency = (
  amount: number | null | undefined,
  currencyCode: string | null | undefined,
  options?: FormatOptions,
): string | null => {
  if (amount == null || currencyCode == null) {
    return null
  }
  const defaultOptions = { symbolize: false, minimumFractionDigits: 2, maximumFractionDigits: 2 }
  const { symbolize, minimumFractionDigits, maximumFractionDigits } = Object.assign(
    {},
    defaultOptions,
    options || {},
  )
  return `${symbolize ? currencyCodeToSymbol(currencyCode) : currencyCode} ${amount.toLocaleString(
    undefined,
    { minimumFractionDigits, maximumFractionDigits },
  )}`
}

/**
 * @deprecated
 * Please use SimpleMoney from utils/simpleMoney.ts
 */
export const formatMoney = (
  money: Maybe<Pick<Money, "subunit" | "currencyCode"> | undefined>,
  options?: FormatOptions,
): string | null => {
  if (!money) {
    return null
  }

  const amount = convertSubunitIntoUnit(money.subunit, money.currencyCode)

  return moneyFormatWithCurrency(amount, money.currencyCode, options)
}

/**
 * Helper function that formats a subunit into a human readable unit
 * @example 30000 cents -> EUR 300
 */
export const moneyFormatSubunit = (
  subunit: number,
  currencyCode: string,
  options?: Intl.NumberFormatOptions,
) => {
  return `${currencyCode} ${convertSubunitIntoUnit(subunit, currencyCode).toLocaleString(
    undefined,
    options,
  )}`
}

/**
 * Returns the subunit value for a specific amount for a specific currency code.
 * If we don't have the subunit ratio, simply return the subunit amount
 * @param subunitAmount number
 * @param currencyCode string
 * @returns number
 */
export const convertSubunitIntoUnit = (subunitAmount: number, currencyCode: string) => {
  const unit = subunitAmount / getSubunitRatio(currencyCode)

  return unit
}

/**
 * Returns the unit form a specific subunit amount. If we don't have the subunit ratio, simply returns the amount
 * @param amount number
 * @param currencyCode string
 * @returns number
 */
export const convertUnitIntoSubunit = (amount: number, currencyCode: string): number => {
  return Math.round(amount * getSubunitRatio(currencyCode))
}

/**
 * Returns the subunitRatio for a currencyCode
 * @param currencyCode string
 * @returns number
 */
export const getSubunitRatio = (currencyCode: string): number => {
  const subunitRatio: number = (currenciesByCurrencyCode as any)[currencyCode]?.subunit_to_unit || 1

  return subunitRatio
}

export const buildMoney = (input?: Partial<MoneyInput>) => {
  const currencyCode = input?.currencyCode || "USD"
  const subunit = input?.subunit || 0
  const subunitToUnit = getSubunitRatio(currencyCode)
  const formatted = ""
  return {
    currencyCode,
    subunit,
    subunitToUnit,
    formatted,
  } as Money
}

export const buildMoneyFromAmount = (
  amount?: number | null,
  currencyCode?: string | null,
): Money | null => {
  if (amount == null || !currencyCode) return null
  const subunit = convertUnitIntoSubunit(amount, currencyCode)
  return buildMoney({ subunit, currencyCode })
}

/**
 * Takes in input string and converts it to use dot separator and allow max of 2 decimal digits
 * Used in moneyinput instead of type=number, so users can copy/paste amounts from other places, using commas and spaces etc
 * @param value input string
 * @returns formatted value
 */

export function convertToMaxDecimals(value: string, maxDecimalDigits: number) {
  let result = value
  // Replace commas with dots
  result = result.replace(",", ".")
  // Exception two dots ..
  result = result.replace(/[.]+/, ".")
  // Remove all forein chars, leave only digits, dots and minus
  result = result.replace(/[^-\d.]*/g, "")
  // Remove multiple minuses
  result = result.replace(/^-+/, "-")
  // Remove minuses that are not at the beginning
  result = result.replace(/(.+)-/, "$1")
  // Remove trailing zeroes
  result = result.replace(/^(-)?0+/, "$10")
  // Remove trailing zeroes before a number like 03
  result = result.replace(/^(-)?0(\d)/, "$1$2")
  // Edge case -.
  result = result.replace("-.", "-0.")
  // Add 0 prefix if input is .x
  result = result.replace(/^-?\.([\d]+)$/g, "0.$1")
  // Remove all digits after N decimals
  const maxDecimalRegexp = new RegExp(`^-?\\d+\\.?\\d{0,${maxDecimalDigits}}$`)
  if (result?.length > 3 && !maxDecimalRegexp.test(result)) {
    result = parseFloat(result).toFixed(maxDecimalDigits)
  }
  return result
}
